import { useState, useMemo } from 'react';
import { Typography, Chip, Stack, Card, FormGroup, Switch, FormControlLabel } from '@mui/material';
import { FilterAlt } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { capitalize } from 'lodash';

import SkeletonContainer from 'components/v2/container/SkeletonContainer';
import { colors } from 'theme/mui/colors';
import { getCaseAssetsApi } from 'api/cases/query';
import { useLocalization } from 'localization';
import MediaViewModal from 'components/v2/media/MediaViewModal';
import { MODAL_ID, MODALS_STORE } from 'constants/ui/modal';
import { getMediaType } from 'utils/getMediaType';
import Materials from '../material/materials/Materials';

const Presentation = () => {
  const { t } = useLocalization();

  const [searchParams] = useSearchParams();
  const [type, setType] = useState({
    category: t('common.terms.all'),
    isActive: true,
  });
  const [selectedId, setSelectedId] = useState('');

  const caseId = searchParams.get('caseId');
  const { data, isLoading } = getCaseAssetsApi(caseId ?? '');
  const { toggleModal, getModalData, isModalOpen } = MODALS_STORE.cases(state => state);
  const currentOpeningFile = getModalData(MODAL_ID.CASES_ASSETS_SHOW_FILE)?.assets?.activeIndex;

  const categories = [t('common.terms.all'), ...new Set(data?.materials.map(img => img.type))];

  const filteredImages = useMemo(() => {
    const materials = data?.materials ?? [];

    if (type.category === t('common.terms.all')) {
      return materials.filter(img => (type.isActive ? Number(img.sortOrder) !== 0 : img));
    }

    if (type.isActive) {
      return materials.filter(img => Number(img.sortOrder) !== 0 && img.type === type.category);
    }

    return materials.filter(img => img.type === type.category);
  }, [data?.materials, type.category, type.isActive]);

  const transformMediaProperties = useMemo(() => {
    if (filteredImages?.length) {
      return filteredImages.map(material => ({
        url: material.source ?? '',
        type: material.source ? getMediaType({ url: material.source }) : undefined,
        title: material.fileName ?? '',
      }));
    }
  }, [filteredImages]);

  const handleCategoryChange = (category: string) => {
    setType({ ...type, category });
  };

  const handleSelect = (id: string) => {
    setSelectedId(id);
    const currentImgIndex = filteredImages?.findIndex(item => item.id === id);
    toggleModal(MODAL_ID.CASES_ASSETS_SHOW_FILE, {
      assets: {
        activeIndex: currentImgIndex,
      },
    });
  };

  const handleActiveMaterials = (event: React.ChangeEvent<HTMLInputElement>) => {
    setType({ ...type, isActive: event.target.checked });
  };

  return (
    <Card sx={{ width: '100%', mt: 10, justifyContent: 'center' }}>
      <Typography>{t('materials.pages.presentation.og.title')}</Typography>
      <SkeletonContainer rows={{ xs: 1 }} columns={{ xs: 1 }} loading={isLoading}>
        <Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Stack alignItems="center" direction="row">
              <FilterAlt sx={{ color: colors.primary.main, mr: 1 }} />
              <Typography variant="h6">{t('tasks.pages.list.filters.category')}</Typography>
            </Stack>
            <Typography variant="body2" color={colors.gray[500]}>
              {filteredImages?.length} {filteredImages?.length === 1 ? 'image' : 'images'}
            </Typography>
          </Stack>

          {data?.materials?.length ? (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ gap: 1, mt: 2 }}>
              {categories.map(category => (
                <Chip
                  key={category}
                  label={capitalize(category)}
                  onClick={() => handleCategoryChange(category)}
                  sx={{
                    px: 1,
                    color: type.category === category ? colors.base.white : 'default',
                    backgroundColor: type.category === category ? colors.primary.main : '',
                    borderRadius: '0.5rem',
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Stack>
          ) : null}
          <FormGroup sx={{ ml: 1.25, mt: 2 }}>
            <FormControlLabel
              sx={{ gap: 1, display: 'flex' }}
              control={<Switch checked={type.isActive} onChange={handleActiveMaterials} />}
              label={t('common.terms.active')}
            />
          </FormGroup>
        </Stack>
        {Boolean(data?.materials?.length) && (
          <Typography variant="h5">
            {type.category === 'all' ? t('common.terms.allAssets') : `${capitalize(type.category)}`}
          </Typography>
        )}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'center', md: 'unset' }}
          gap={2.75}
          justifyContent="center"
          flexWrap="wrap"
        >
          {filteredImages?.length ? (
            <Materials
              imgStyles={{
                maxHeight: 250,
                objectFit: 'cover',
              }}
              visualMaterials={filteredImages}
              currentSelectedId={[selectedId]}
              onItemClick={handleSelect}
            />
          ) : (
            <Typography variant="h6">{t('modals.virtualStaging.emptyList')}</Typography>
          )}
        </Stack>
      </SkeletonContainer>
      {isModalOpen(MODAL_ID.CASES_ASSETS_SHOW_FILE) && (
        <MediaViewModal
          onClose={() => toggleModal(MODAL_ID.CASES_ASSETS_SHOW_FILE)}
          isOpen={isModalOpen(MODAL_ID.CASES_ASSETS_SHOW_FILE)}
          mediaItems={transformMediaProperties ?? []}
          activeIndex={currentOpeningFile}
        />
      )}
    </Card>
  );
};
export default Presentation;
